import Link from 'next/link'
import { Button } from '@/components/ui/button'
import { FileText, GitBranch, Sparkles } from 'lucide-react'

interface BigPromoCardProps {
  buildLoginRedirect: (target: string) => string
  isVisible: boolean
}

export default function BigPromoCard({ buildLoginRedirect, isVisible }: BigPromoCardProps) {
  return (
    <section
      className={`w-full bg-white px-0 py-16 sm:px-6 md:py-20 lg:px-8 transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
    >
      {/* ✅ 모바일: w-full(꽉참) / sm+: max-width 중앙정렬 */}
      <div className="w-full sm:mx-auto sm:max-w-6xl">
        {/* ✅ 모바일: 라운드 없음 / sm+: 라운드 적용 + 그라데이션 배경 */}
        <div className="relative overflow-hidden bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 text-white sm:rounded-3xl">
          {/* 비네팅 효과 */}
          <div 
            className="pointer-events-none absolute inset-0 opacity-50"
            style={{
              background: 'radial-gradient(ellipse at center, transparent 0%, rgba(0,0,0,0.3) 100%)'
            }}
          />
          
          <div className="relative grid gap-8 p-8 md:grid-cols-2 md:items-start md:gap-12 md:p-12">
            {/* Left */}
            <div className="flex flex-col">
              <h2 className="mb-3 text-3xl font-bold leading-tight md:text-4xl">
                제안서가 쉬워지면
                <br />
                수주 속도가 빨라집니다
              </h2>
              <p className="mb-6 text-base leading-relaxed text-gray-300 md:text-lg">
                링커스는 프리랜서가 실제로 원하는 "제안서 제출"에 맞춰,
                핵심 정보 정리부터 수정·재제출까지 빠르게 진행할 수 있게 설계했습니다.
              </p>
              
              {/* CTA 버튼 */}
              <div className="flex flex-col items-start gap-2">
                <Button
                  asChild
                  className="h-12 w-full whitespace-nowrap rounded-xl bg-blue-600 px-4 text-center text-sm font-semibold leading-none text-white shadow-lg transition-all hover:bg-blue-700 hover:shadow-xl md:w-auto md:px-8 md:text-base"
                >
                  <Link
                    href={buildLoginRedirect('/onboarding/freelancer/role')}
                    data-landing-event="click_cta_promo_primary"
                    className="inline-flex w-full items-center justify-center whitespace-nowrap"
                  >
                    지금 프로필 등록하기
                  </Link>
                </Button>

                <Link
                  href="/freelancer/projects"
                  data-landing-event="click_secondary_explore"
                  className="inline-flex items-center text-sm font-medium text-white/80 underline underline-offset-4 transition-colors hover:text-white"
                >
                  제안 기회 둘러보기
                </Link>
              </div>
            </div>

            {/* Right - Benefit Cards */}
            <div className="flex flex-col gap-4 md:pt-0">
              {/* 제안서 구조화 카드 */}
              <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/10 p-5 backdrop-blur-sm transition-all hover:bg-white/15 hover:border-white/20">
                {/* 상단 accent 라인 */}
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-blue-500" />
                
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 rounded-xl bg-blue-500/20 p-2.5">
                    <FileText className="h-5 w-5 text-blue-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-gray-400">
                      제안서 카드 구성
                    </div>
                    <div className="mb-1.5 text-lg font-bold text-white">
                      가격/기간/산출물/범위 한 장 요약
                    </div>
                    <div className="text-xs leading-relaxed text-gray-400">
                      클라이언트가 바로 이해하는 포맷으로 전달
                    </div>
                  </div>
                </div>
              </div>

              {/* 제안서 플로우 카드 */}
              <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/10 p-5 backdrop-blur-sm transition-all hover:bg-white/15 hover:border-white/20">
                {/* 상단 accent 라인 */}
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-blue-500" />
                
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 rounded-xl bg-blue-500/20 p-2.5">
                    <GitBranch className="h-5 w-5 text-blue-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-gray-400">
                      Draft → Submit → Revision
                    </div>
                    <div className="mb-1.5 text-lg font-bold text-white">
                      저장/수정이 쉬운 제출 흐름
                    </div>
                    <div className="text-xs leading-relaxed text-gray-400">
                      초안 저장 후 빠르게 보완하고 재제출
                    </div>
                  </div>
                </div>
              </div>

              {/* 자동 요약 보조 카드 */}
              <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/10 p-5 backdrop-blur-sm transition-all hover:bg-white/15 hover:border-white/20">
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-blue-500" />

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 rounded-xl bg-blue-500/20 p-2.5">
                    <Sparkles className="h-5 w-5 text-blue-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-gray-400">
                      요청 내용 → 요약 자동 생성
                    </div>
                    <div className="mb-1.5 text-lg font-bold text-white">
                      핵심만 빠르게 정리
                    </div>
                    <div className="text-xs leading-relaxed text-gray-400">
                      긴 요청문을 제안서 초안으로 정리해 작성 시간을 단축
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
